import React, { useMemo } from 'react'; // Removed unused useState
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Box,
  Button,
  IconButton,
  Modal as MuiModal,
  Stack,
  Typography, // Removed TextField
  // Removed Switch
  Paper,
  // Removed FormControl
  // Removed Select
  // Removed MenuItem
  // Removed InputLabel
  // Removed Tabs
  // Removed Tab
  // Removed Tooltip
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
// Removed unused HelpOutlineIcon
import { useSelector } from 'react-redux';
import { removeEmptyValuesFromObject, removeNullValueFromObject } from '../../../../../utility/object-related';

// Import UTC plugin for dayjs
import utcPlugin from 'dayjs/plugin/utc'; // Changed require to import
dayjs.extend(utcPlugin);


const centeredMuiModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: {
    xs: 3 / 4,
    md: 1 / 2,
    lg: 1 / 3,
  },
  bgcolor: "background.paper",
  border: "2px solid #000",
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
};

// Removed unused DATA_TYPES constant

/**
 * @typedef {Object} ExportReportsProps
 * @property {boolean} exportReportsOpen - Controls the visibility of the modal
 * @property {() => void} handleExportReportsClose - Handler for closing the modal
 * @property {boolean} exportLoading - Indicates if export operation is in progress
 * @property {() => void} onExport - Function to export
 * @property {boolean} showFilters - Controls whether filters are shown
 * @property {Object} exportDateRange - Current export date range
 * @property {Function} setExportDateRange - Function to update export date range
 * @property {Object} filter - Current filter configuration
 * @property {Function} setFilter - Function to update filter configuration
 * @property {Array} selectedRows - Selected rows
 */

/**
 * Modal component for exporting allocations within a specified date range
 * @param {ExportReportsProps} props
 */
const ExportReports = ({
  exportReportsOpen = false,
  handleExportReportsClose,
  exportLoading = false,
  onExport,
  showFilters = false,
  filter = {},
  setFilter,
  exportDateRange,
  setExportDateRange,
  selectedRows = []
}) => {

  const properFilters = useMemo(() => {
    return removeEmptyValuesFromObject(filter);
  }, [filter])

  const { users, Districts } = useSelector(state => state.user || {}) // Removed unused roles, Languages


  const handleExportReports = () => {
    onExport();
  };


  const handleClose = (event, reason) => {
    const notAllowedReasons = ['escapeKeyDown', 'backdropClick'];
    if (notAllowedReasons.includes(reason)) {
      return false;
    }
    handleExportReportsClose();
    return true;
  }

  return (
    <MuiModal
      open={exportReportsOpen}
      onClose={handleClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box sx={centeredMuiModalStyle}>
        <IconButton
          disabled={exportLoading}
          aria-label="close"
          onClick={handleExportReportsClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography id="modal-title" variant="h6" component="h2">
          Export Reports
        </Typography>
        <Stack
          direction="column"
          spacing={2}
          sx={{
            p: 2,
            border: "2px solid #e0e0e0",
            borderRadius: 2,
          }}
        >


          <Stack direction="column">
            {/* <Typography>Select Date Range</Typography> */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Stack direction="row" spacing={2} justifyContent="center" pt={1}>
                <DatePicker
                  disabled
                  label="From"
                  value={exportDateRange?.from ? dayjs(exportDateRange.from) : null}
                  onChange={(newValue) => {
                    setExportDateRange({
                      ...exportDateRange,
                      from: newValue ? newValue.utc().format('YYYY-MM-DD') : null,
                    });
                  }}
                  slotProps={{ textField: { size: 'small' } }} // Optional: Adjust size if needed
                />
                <DatePicker
                  disabled
                  label="To"
                  value={exportDateRange?.to ? dayjs(exportDateRange.to) : null}
                  onChange={(newValue) => {
                    setExportDateRange({
                      ...exportDateRange,
                      to: newValue ? newValue.utc().format('YYYY-MM-DD') : null,
                    });
                  }}
                  slotProps={{ textField: { size: 'small' } }} // Optional: Adjust size if needed
                />
              </Stack>
            </LocalizationProvider>
          </Stack>

          {showFilters && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: 'grey.100',
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 2
              }}
            >
              <Typography variant="subtitle1">Current Filters:</Typography>
              <Box sx={{ p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                {Object.entries(properFilters).map(([key, value]) => {
                  if (!Array.isArray(value)) {
                    // Render single value, formatting dates
                    const displayValue = (key === 'from' || key === 'to') && value ? dayjs(value).format('YYYY-MM-DD') : value;
                    return (
                      <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                        <strong>{key}:</strong> {displayValue}
                      </Typography>
                    );
                  } else {
                    // Render array value in an Accordion
                    return (
                      <Accordion key={key} sx={{ '&.MuiAccordion-root:before': { display: 'none' }, boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.12)', mb: 1 }}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls={`${key}-content`}
                          id={`${key}-header`}
                          sx={{ minHeight: '36px', '& .MuiAccordionSummary-content': { margin: '8px 0' } }}
                        >
                          <Typography variant="body2"><strong>{key}</strong> ({value.length})</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {value.map((item, index) => {
                            let displayValue = item;
                            if (key === 'district') {
                              const district = Districts && Districts.find(d => d._id === item); // Check Districts exists
                              displayValue = district ? district.name : `Unknown District (${item})`;
                            } else if (key === 'allocatedToUser') {
                              const user = users && users.find(u => u._id === item); // Check users exists
                              displayValue = user ? user.name : `Unknown User (${item})`;
                            }
                            return (
                              <Typography key={`${key}-${index}`} variant="caption">
                                {displayValue}
                              </Typography>
                            );
                          })}
                        </AccordionDetails>
                      </Accordion>
                    );
                  }
                })}
                {Object.keys(properFilters).length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No filters applied
                  </Typography>
                )}
              </Box>

            </Paper>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 1 }}>
            {selectedRows.length > 0
              ? `Exporting ${selectedRows.length} selected report(s).`
              : 'Exporting reports within the selected date range and filters.'}
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              onClick={handleExportReports}
              variant="contained"
              disabled={exportLoading}
            >
              {exportLoading ? "Exporting..." : "Export"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </MuiModal>
  );
};

ExportReports.propTypes = {
  exportReportsOpen: PropTypes.bool,
  handleExportReportsClose: PropTypes.func.isRequired,
  exportLoading: PropTypes.bool,
  onExport: PropTypes.func.isRequired,
  showFilters: PropTypes.bool,
  exportDateRange: PropTypes.object,
  setExportDateRange: PropTypes.func.isRequired,
  filter: PropTypes.object,
  setFilter: PropTypes.func.isRequired,
  selectedRows: PropTypes.array
};

export default ExportReports;